/**
 * Sprint 11 Phase 3b — CoachingReportPanel
 *
 * Lives inside IndividualClientView, only renders when
 * `status === 'completed'`. Three buttons:
 *  - Generate report  → POST /coaching-report (renders PDF + sets pdf_generated_at)
 *  - Download         → /coaching-report/download?token=...  (signed)
 *  - Send to client   → 2-step modal → POST /coaching-report/send
 *
 * Send button is gated on `report_pdf_generated_at IS NOT NULL`
 * (you can't send a PDF that hasn't been rendered yet). Post-send
 * the button label flips to "Sent N days ago · Re-send?" matching
 * the Phase 1 link-button pattern.
 */
import React from "react";
import { api, formatApiError } from "../lib/api";

function relativeFromNow(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const diff = Date.now() - then;
  if (diff < 60000) return "just now";
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr === 1 ? "1 hour ago" : `${hr} hours ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return d === 1 ? "yesterday" : `${d} days ago`;
  return new Date(iso).toLocaleDateString();
}

function formatLongDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function shortSha(sha) {
  if (!sha) return "";
  return `${sha.slice(0, 8)}…${sha.slice(-4)}`;
}

export default function CoachingReportPanel({ deploymentId, clientName, report }) {
  const [generating, setGenerating] = React.useState(false);
  const [downloadToken, setDownloadToken] = React.useState(null);
  const [pdfGeneratedAt, setPdfGeneratedAt] = React.useState(report?.pdf_generated_at || null);
  const [reportSentAt, setReportSentAt] = React.useState(report?.report_sent_at || null);
  const [error, setError] = React.useState("");
  const [sendPreview, setSendPreview] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [snapshots, setSnapshots] = React.useState([]);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [shaShownFor, setShaShownFor] = React.useState(null);

  const refreshSnapshots = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/api/deployments/${deploymentId}/coaching-report/snapshots`);
      setSnapshots(data?.snapshots || []);
    } catch (_) {
      // Listing is non-critical; main flow stays usable if it fails.
    }
  }, [deploymentId]);

  React.useEffect(() => { refreshSnapshots(); }, [refreshSnapshots]);

  async function onGenerate() {
    setError("");
    setGenerating(true);
    try {
      const { data } = await api.post(`/api/deployments/${deploymentId}/coaching-report`);
      setPdfGeneratedAt(data.report_pdf_generated_at);
      setDownloadToken(data.download_token);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  }

  async function mintDownloadToken() {
    // The download endpoint is token-gated (signed JWT in the URL).
    // We mint a fresh one by re-posting to /coaching-report, which is
    // idempotent. Sprint 12 — the download endpoint now always renders
    // fresh from current state (current scores, current published plan,
    // current copy) so the coach sees their latest edits. For retrieving
    // the byte-exact PDF that was emailed at any prior send, use the
    // per-version snapshot links in Send history.
    const { data } = await api.post(`/api/deployments/${deploymentId}/coaching-report`);
    setPdfGeneratedAt(data.report_pdf_generated_at);
    setDownloadToken(data.download_token);
    return data.download_token;
  }

  async function onDownload() {
    setError("");
    try {
      // Cover the post-refresh / cross-session case where local state
      // lost the token but the server still has a generated (or
      // frozen) PDF. Mint a token on the fly. Pre-2026-02-20 the
      // Download button silently disappeared in this case — Send
      // (server-gated) worked, Download (local-state-gated) did not.
      const t = downloadToken || (await mintDownloadToken());
      const url = `${api.defaults.baseURL || ""}/api/deployments/${deploymentId}/coaching-report/download?token=${encodeURIComponent(t)}`;
      window.open(url, "_blank");
    } catch (e) {
      setError(formatApiError(e));
    }
  }

  async function openSendPreview() {
    setError("");
    try {
      const { data } = await api.get(`/api/deployments/${deploymentId}/coaching-report/send/preview`);
      setSendPreview(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }

  async function confirmSend() {
    setSending(true);
    setError("");
    try {
      const { data } = await api.post(`/api/deployments/${deploymentId}/coaching-report/send`);
      if (data.ok) {
        setReportSentAt(data.sent_at);
        setSendPreview(null);
        refreshSnapshots();
      } else {
        setError(data.error || "Send failed.");
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSending(false);
    }
  }

  const sendLabel = reportSentAt
    ? `Sent ${relativeFromNow(reportSentAt)} · Re-send`
    : `Send report to ${clientName || "client"}`;

  return (
    <section
      data-testid="coaching-report-panel"
      className="p-6"
      style={{ background: "#FBFAF7", border: "1px solid #E0E0E0" }}
    >
      <div className="eyebrow mb-2" style={{ color: "#2563EB" }}>Coaching report</div>
      <p className="text-sm text-gray-700 max-w-prose mb-4">
        Generate the identified PDF for {clientName}&rsquo;s coaching debrief. The report
        uses your practice&rsquo;s branding on the cover. Send it after you&rsquo;ve had a
        chance to read it yourself.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating}
          data-testid="generate-coaching-report"
          style={{
            fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
            fontSize: "11px", color: "#FBFAF7",
            background: generating ? "#999" : "#1A1A1A",
            border: 0, padding: "10px 16px", cursor: generating ? "wait" : "pointer",
          }}
        >
          {generating ? "Rendering…" : pdfGeneratedAt ? "Re-generate report" : "Generate report"}
        </button>

        {pdfGeneratedAt && (
          <button
            type="button"
            onClick={onDownload}
            data-testid="download-coaching-report"
            className="btn-quiet"
          >
            Download PDF
          </button>
        )}

        <button
          type="button"
          onClick={openSendPreview}
          disabled={!pdfGeneratedAt}
          data-testid="open-send-coaching-report"
          title={pdfGeneratedAt ? "" : "Generate the report first"}
          style={{
            fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
            fontSize: "11px",
            color: pdfGeneratedAt ? "#FBFAF7" : "#999",
            background: pdfGeneratedAt ? "#2563EB" : "#E0E0E0",
            border: 0, padding: "10px 16px",
            cursor: pdfGeneratedAt ? "pointer" : "not-allowed",
          }}
        >
          {sendLabel}
        </button>
      </div>

      {(pdfGeneratedAt || reportSentAt) && (
        <div className="text-xs text-gray-500 mt-3 space-y-1">
          {pdfGeneratedAt && <div data-testid="pdf-generated-at">PDF generated {relativeFromNow(pdfGeneratedAt)}</div>}
          {reportSentAt && <div data-testid="report-sent-at">Sent to client {relativeFromNow(reportSentAt)}</div>}
        </div>
      )}

      {error && (
        <div className="text-xs mt-3" data-testid="coaching-report-error" style={{ color: "#AD393E" }}>{error}</div>
      )}

      {snapshots.length > 0 && (
        <div className="mt-5 pt-4" style={{ borderTop: "1px solid #E0E0E0" }} data-testid="coaching-report-history">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            data-testid="toggle-coaching-history"
            style={{
              fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
              fontSize: "10px", color: "#6F6F6F", background: "transparent",
              border: 0, padding: 0, cursor: "pointer",
            }}
          >
            {historyOpen ? "▾" : "▸"} Send history ({snapshots.length})
          </button>
          {historyOpen && (
            <ol className="mt-3 space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {snapshots.map((s) => {
                const shaOpen = shaShownFor === s.id;
                return (
                  <li
                    key={s.id}
                    data-testid={`coaching-history-row-${s.version}`}
                    className="text-sm"
                    style={{ paddingLeft: "0.25rem" }}
                  >
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span style={{ color: "#1A1A1A" }}>
                        v{s.version} sent {formatLongDate(s.sent_at)}
                      </span>
                      <a
                        href={`${api.defaults.baseURL || ""}/api/deployments/${deploymentId}/coaching-report/snapshots/${s.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`coaching-history-download-${s.version}`}
                        style={{
                          fontFamily: "'Helvetica Neue', Arial, sans-serif",
                          fontSize: "10px", letterSpacing: "0.06em",
                          color: "#2563EB", background: "transparent",
                          padding: 0, textDecoration: "underline",
                        }}
                      >
                        download original
                      </a>
                      <button
                        type="button"
                        onClick={() => setShaShownFor(shaOpen ? null : s.id)}
                        data-testid={`coaching-history-details-${s.version}`}
                        style={{
                          fontFamily: "'Helvetica Neue', Arial, sans-serif",
                          fontSize: "10px", letterSpacing: "0.06em",
                          color: "#6F6F6F", background: "transparent",
                          border: 0, padding: 0, cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {shaOpen ? "hide details" : "details"}
                      </button>
                    </div>
                    {shaOpen && (
                      <div
                        className="text-xs mt-1"
                        style={{ color: "#7F7F7F", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                        data-testid={`coaching-history-sha-${s.version}`}
                      >
                        sha256 {shortSha(s.sha256)} · {(s.byte_size / 1024).toFixed(0)} KB
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {sendPreview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-12"
          style={{ background: "rgba(26,26,26,0.4)" }}
          data-testid="send-coaching-report-modal"
          onClick={() => !sending && setSendPreview(null)}
        >
          <div className="bg-paper p-8 w-full max-w-xl" style={{ border: "1px solid #DDDDDD" }} onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow mb-2" style={{ color: "#2563EB" }}>Send preview</div>
            <h3 className="text-xl mb-3" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
              {reportSentAt ? "Re-send" : "Send"} coaching report to {sendPreview.client_name}
            </h3>
            <p className="text-xs text-gray-600 mb-4">
              To: <strong>{sendPreview.to}</strong> · From: noreply@resilientteams.io · Attachment: coaching-report.pdf
            </p>
            <div className="mb-4">
              <div className="label">Subject</div>
              <div className="text-sm py-2 px-3" style={{ background: "white", border: "1px solid #E0E0E0" }} data-testid="send-coaching-subject">
                {sendPreview.subject}
              </div>
            </div>
            <div className="mb-4">
              <div className="label">Body</div>
              <pre
                className="text-sm py-3 px-3 whitespace-pre-wrap leading-relaxed"
                style={{ background: "white", border: "1px solid #E0E0E0", fontFamily: "inherit", maxHeight: "240px", overflowY: "auto" }}
                data-testid="send-coaching-body"
              >{sendPreview.body}</pre>
            </div>
            {sendPreview.previously_sent_at && (
              <p className="text-xs mb-3" style={{ color: "#7F7F7F" }}>
                Previously sent {new Date(sendPreview.previously_sent_at).toLocaleString()} — re-sending generates a fresh PDF.
              </p>
            )}
            {!sendPreview.email_configured && (
              <p className="text-xs mb-3" style={{ color: "#AD393E" }} data-testid="send-coaching-unconfigured">
                Email service is not configured on this environment.
              </p>
            )}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setSendPreview(null)} disabled={sending} className="btn-quiet" data-testid="send-coaching-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSend}
                disabled={sending || !sendPreview.email_configured}
                data-testid="send-coaching-confirm"
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                  fontSize: "11px", color: "#FBFAF7",
                  background: sending ? "#999" : "#2563EB",
                  border: 0, padding: "10px 18px", cursor: sending ? "wait" : "pointer",
                }}
              >
                {sending ? "Sending…" : "Confirm and send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
